import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { PosTask } from "../../interfaces/Task";
import CustomEditor from "../../elements/CustomEditor";
import Help from "../../elements/HelpContent";
import { useNavigate } from "react-router-dom";
import AITools from "../../elements/AITools";
import CloneAs from "../../elements/CloneAs";
import { useTranslation } from "react-i18next";
import { Overlay } from "../../elements/Overlay";

const emptyPosTask: any = {
    _id: '0',
    name: '',
    updated:new Date(),
    type: 'dialog',
    instructions: '',
    text: ''
}

export interface EditPosTaskDialogProps {
    task_id: string;
    id: string;
    position: number;
    saveForm: CallableFunction;
    closeForm: CallableFunction;
    cloneAs: CallableFunction;
    task_language?: string;
}

export default function EditPosTaskDialog(props: EditPosTaskDialogProps) {
    const { t } = useTranslation();
    
    const navigate = useNavigate();
    const [posTask, setPosTask] = useState<PosTask>(emptyPosTask);
    const [name, setName] = useState('');
    const [instructions, setInstructions] = useState('');
    const [teacher_notes, setTeacherNotes] = useState('');
    const [text, setText] = useState('');
    const [formattedText, setFormattedText] = useState('');
    
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    }

    const handleInstructionsChange = (data: string) => {
        setInstructions(data);
    }

    const handleTeacherNotesChange = (data: string) => {
        setTeacherNotes(data);
    }

    const handleTextChange = (data: string) => {
        setText(data);
    }

    const close = () => {
        props.closeForm();
    }

    const save = () => {
        if (name) {
            posTask.name = name;
            posTask.instructions = instructions;
            posTask.text = text;
            posTask.formatted_text = formattedText;
            posTask.teacher_notes = teacher_notes;
            props.saveForm(posTask);
        }
    }

    useEffect(() => {
        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task/' + props.task_id + '/postask/' + props.id, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                setPosTask(data);
                setName(data.name);
                setFormattedText(data.formatted_text);
            });
    }, []);

    const handleCloneAs = (id: string, direction: string) => {
        props.cloneAs(id, direction);
    }

    const handleSaveFormattedText = (text: string) => {
        setFormattedText(text);
    }

    const doNothing = () => {
        
    }

    return(
        <Overlay
            title={ t("Edit Post-Task: Written Dialogue") }
            onClose={ close }
            actions={[
                {
                    label: t("Cancel"),
                    variant: 'secondary',
                    onClick: close
                },
                {
                    label: t("Save"),
                    variant: 'primary',
                    onClick: save
                }
            ]}
        >
            <div className="new-task-container writen-dialog">
                <div className="inner">
                    <h2>
                        { t("Edit Post-Task: Written Dialogue") }
                        <Help id={ '652a97011e5cf99991b54080' } />
                    </h2>
                    <AITools />
                    <Form className="new-post-task-form">
                        <div className="fields-container">
                            <Form.Group className="field field-title">
                                <Form.Label>{ t("Title of the Post-Task") }</Form.Label>
                                <Form.Control type="text" name="name" required value={ name } onChange={ handleNameChange } />
                            </Form.Group>
                            <div className="field field-instructions">
                                <CustomEditor
                                    change={ handleInstructionsChange }
                                    changeFormatted={ doNothing }
                                    data={ posTask.instructions }
                                    formatted= { '' }
                                    id="instructions"
                                    label={ t("Aim and instructions") }
                                    npl={ false }
                                    task_id={ '' }
                                />
                            </div>
                            <div className="field field-teachernotes">
                                <CustomEditor
                                    change={ handleTeacherNotesChange }
                                    changeFormatted={ doNothing }
                                    data={ posTask.teacher_notes }
                                    formatted= { '' }
                                    id="teachernotes"
                                    label={ t("Teacher notes") }
                                    npl={ false }
                                    task_id={ '' }
                                />
                            </div>
                            <div className="field field-text">
                                <CustomEditor
                                    change={ handleTextChange }
                                    changeFormatted={ handleSaveFormattedText }
                                    data={ posTask.text }
                                    formatted={ posTask.formatted_text }
                                    id="text"
                                    label={ t("Text")}
                                    npl={ true }
                                    task_id={ props.task_id }
                                    task_language={ props.task_language }
                                />
                            </div>
                            <CloneAs id={ props.id } direction={ 'pos-to-pre' } cloneAs={ handleCloneAs } />
                        </div>
                    </Form>
                </div>
            </div>
        </Overlay>
    );
}